"use client";

import { useRef, useState, useEffect, useCallback, useId } from "react";
import { ChevronDown, Check } from "lucide-react";
import type { FestivalDay } from "@/lib/types";
import { useScheduleData } from "./use-schedule-data";
import { ScheduleGrid, StageHeaders, TimeGutterOverlay } from "./schedule-grid";

/** Desktop drag-to-scroll: grab empty space to pan horizontally. */
function useDragScroll(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;

    function onDown(e: PointerEvent) {
      // Only left button, only on desktop (not touch)
      if (e.button !== 0 || e.pointerType === "touch") return;
      // Skip if the click target is inside an interactive element or schedule block
      const target = e.target as HTMLElement;
      if (target.closest("a, button, [data-schedule-block]")) return;

      isDragging = true;
      startX = e.clientX;
      scrollStart = el!.scrollLeft;
      el!.setPointerCapture(e.pointerId);
      el!.style.cursor = "grabbing";
      el!.style.userSelect = "none";
    }

    function onMove(e: PointerEvent) {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      el!.scrollLeft = scrollStart - dx;
    }

    function onUp(e: PointerEvent) {
      if (!isDragging) return;
      isDragging = false;
      el!.releasePointerCapture(e.pointerId);
      el!.style.cursor = "";
      el!.style.userSelect = "";
    }

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
  }, [ref]);
}

/** Syncs horizontal scroll between two elements. */
function useSyncScroll(
  refA: React.RefObject<HTMLElement | null>,
  refB: React.RefObject<HTMLElement | null>
) {
  useEffect(() => {
    const a = refA.current;
    const b = refB.current;
    if (!a || !b) return;

    let syncing = false;

    function onScrollA() {
      if (syncing) return;
      syncing = true;
      b!.scrollLeft = a!.scrollLeft;
      syncing = false;
    }

    function onScrollB() {
      if (syncing) return;
      syncing = true;
      a!.scrollLeft = b!.scrollLeft;
      syncing = false;
    }

    a.addEventListener("scroll", onScrollA);
    b.addEventListener("scroll", onScrollB);
    return () => {
      a.removeEventListener("scroll", onScrollA);
      b.removeEventListener("scroll", onScrollB);
    };
  }, [refA, refB]);
}

/** Multi-select venue dropdown filter. */
function StageFilter({
  stages,
  selected,
  onToggle,
  onSelectAll,
  onSelectNone,
  variant = "venues",
}: {
  stages: string[];
  selected: Set<string>;
  onToggle: (stage: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  variant?: "stages" | "venues";
}) {
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const noun = variant === "stages" ? "Stages" : "Venues";
  const allSelected = selected.size === stages.length;
  const noneSelected = selected.size === 0;
  const label = allSelected
    ? `All ${noun}`
    : noneSelected
      ? `No ${noun}`
      : `${selected.size} of ${stages.length} ${noun}`;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(!open)}
        className="flex cursor-pointer items-center gap-1.5 rounded border border-neutral-700 bg-dark py-1 pl-2 pr-1.5 font-mono text-[11px] text-neutral-300 outline-none transition-colors hover:border-neutral-500 focus:border-acid/50"
      >
        <span>{label}</span>
        <ChevronDown
          size={12}
          strokeWidth={2}
          className={`shrink-0 text-neutral-500 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          className="absolute right-0 top-full z-50 mt-1 max-h-72 min-w-48 overflow-auto rounded border border-neutral-700 bg-dark py-0.5 shadow-xl shadow-black/50 schedule-scrollbar"
        >
          {/* Select All / None */}
          <li className="flex gap-1 border-b border-neutral-800 px-2 py-1.5">
            <button
              onClick={onSelectAll}
              className="font-mono text-[10px] text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              All
            </button>
            <span className="text-neutral-700 text-[10px]">/</span>
            <button
              onClick={onSelectNone}
              className="font-mono text-[10px] text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              None
            </button>
          </li>

          {stages.map((stage) => {
            const isActive = selected.has(stage);
            return (
              <li
                key={stage}
                role="option"
                aria-selected={isActive}
                onClick={() => onToggle(stage)}
                className="flex cursor-pointer items-center gap-2 px-2 py-1 font-mono text-[11px] transition-colors hover:bg-neutral-800/80 hover:text-white text-neutral-400"
              >
                <span className={`flex h-3 w-3 shrink-0 items-center justify-center rounded-sm border ${
                  isActive ? "border-acid bg-acid/20" : "border-neutral-600"
                }`}>
                  {isActive && <Check size={8} strokeWidth={3} className="text-acid" />}
                </span>
                <span className="truncate">{stage}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const STAGE_FILTER_THRESHOLD = 10;

export { StageFilter, STAGE_FILTER_THRESHOLD };

export function ScheduleView({
  day,
  blockedSpotifyIds,
  festivalId,
  showOnlyMySchedule,
  stageVenues,
  visibleStages: visibleStagesOverride,
}: {
  day: FestivalDay;
  blockedSpotifyIds: string[];
  festivalId: number;
  showOnlyMySchedule: boolean;
  stageVenues?: Record<string, { slug: string; citySlug: string }>;
  /** When provided, overrides which stages are visible (for external filtering). */
  visibleStages?: string[];
}) {
  const data = useScheduleData(day.attractions);
  const scrollRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  useDragScroll(scrollRef);
  useSyncScroll(topScrollRef, scrollRef);
  useSyncScroll(headerScrollRef, scrollRef);

  const visibleStages = visibleStagesOverride ?? data?.stages ?? [];

  // Keep top scrollbar width in sync with the grid content width
  const updateTopWidth = useCallback(() => {
    if (innerRef.current && topScrollRef.current) {
      const sizer = topScrollRef.current.firstElementChild as HTMLElement | null;
      if (sizer) sizer.style.width = `${innerRef.current.scrollWidth}px`;
    }
  }, []);

  useEffect(() => {
    updateTopWidth();
    const obs = new ResizeObserver(updateTopWidth);
    if (innerRef.current) obs.observe(innerRef.current);
    return () => obs.disconnect();
  }, [updateTopWidth, data]);

  if (!data) return null;

  // Compute grid metrics (same formula as ScheduleGrid)
  const MIN_BLOCK_PX = 60;
  const minDuration = data.slots.length > 0
    ? Math.min(...data.slots.map((s) => s.durationMinutes))
    : 30;
  const pixelsPerMinute = Math.min(5, Math.max(2, Math.ceil(MIN_BLOCK_PX / minDuration)));
  const gridHeight = (data.dayEndMinutes - data.dayStartMinutes) * pixelsPerMinute;

  return (
    <div>
      {/* Stage headers — sticky at top, scroll synced with grid */}
      <div
        ref={headerScrollRef}
        className="sticky top-0 z-20 overflow-x-hidden bg-dark/95 backdrop-blur-sm"
      >
        <StageHeaders stages={visibleStages} stageVenues={stageVenues} />
      </div>

      {/* Top scrollbar — mirrors the main grid's horizontal scroll */}
      <div
        ref={topScrollRef}
        className="overflow-x-auto schedule-scrollbar mb-2"
        style={{ height: 14 }}
      >
        <div style={{ height: 1 }} />
      </div>

      {/* Grid + sticky time gutter */}
      <div className="relative">
        {/* Time gutter overlay — stays pinned on the left, outside the scroll container */}
        <TimeGutterOverlay
          dayStartMinutes={data.dayStartMinutes}
          dayEndMinutes={data.dayEndMinutes}
          pixelsPerMinute={pixelsPerMinute}
          gridHeight={gridHeight}
        />

        {/* Horizontal scroll for wide grids */}
        <div
          ref={scrollRef}
          className="overflow-x-auto overflow-y-hidden schedule-scrollbar cursor-grab"
        >
          <div ref={innerRef}>
            <ScheduleGrid
              data={data}
              visibleStages={visibleStages}
              festivalDate={day.date}
              blockedSpotifyIds={blockedSpotifyIds}
              festivalId={festivalId}
              eventId={day.event_id}
              showOnlyMySchedule={showOnlyMySchedule}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
